import { motion } from "framer-motion";
import { Flame, ArrowRight, Leaf, ShieldCheck, ExternalLink } from "lucide-react";
import { PINKSALE_URL } from "@/lib/links";
import { isPresaleLive, PRESALE_START_LABEL } from "@/lib/presale";
import { BuyPinkSaleButton } from "@/components/BuyPinkSaleButton";

const HERO_BG =
  "https://images.unsplash.com/photo-1700938550585-50ab9f83e075?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYWJzdHJhY3QlMjBzbW9rZSUyMGVtYmVyfGVufDB8fHx8MTc4Mjg2MTYxOHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const live = isPresaleLive();
  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/60" />

      <div className="relative px-6 md:px-12 max-w-7xl mx-auto w-full pt-28 pb-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-ember/40 px-4 py-2 mb-8"
          >
            <Flame className="w-4 h-4 text-ember" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">
              $AFTBRN · Regeneration Token
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold tracking-tighter leading-[0.95]"
          >
            Turning <span className="text-red-500">ashes</span>
            <br />
            into <span className="text-emerald-400 ember-text-glow">forests.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl"
          >
            AfterBurn is a community-powered crypto token funding wildfire recovery — financing
            reforestation, replanting, and the reconstruction of the communities left behind.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            {live ? (
              <>
                <BuyPinkSaleButton
                  testId="hero-pinksale-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-ember text-black font-bold px-8 py-4 hover:bg-ember-light transition-colors"
                />
                <a
                  href="#presale"
                  data-testid="hero-presale-btn"
                  className="inline-flex items-center justify-center gap-2 border border-muted-foreground/60 px-8 py-4 hover:border-foreground transition-colors"
                >
                  Get Your Free Badge
                </a>
              </>
            ) : (
              <a
                href="#presale"
                data-testid="hero-waitlist-btn"
                className="group inline-flex items-center justify-center gap-2 bg-ember text-black font-bold px-8 py-4 hover:bg-ember-light transition-colors"
              >
                <Flame className="w-4 h-4" />
                Join the Waitlist
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
            <a
              href="#mission"
              data-testid="hero-mission-btn"
              className="inline-flex items-center justify-center gap-2 border border-muted-foreground/60 px-8 py-4 hover:border-foreground transition-colors"
            >
              <Leaf className="w-4 h-4 text-forest-light" />
              Read the Mission
            </a>
          </motion.div>

          {!live && (
            <motion.a
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              href={PINKSALE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-verify-pinksale-link"
              className="mt-5 inline-flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-ember transition-colors"
            >
              <ShieldCheck className="w-4 h-4 text-forest-light" />
              Verify our presale on PinkSale — opens {PRESALE_START_LABEL}
              <ExternalLink className="w-3.5 h-3.5" />
            </motion.a>
          )}
        </div>
      </div>
    </section>
  );
}
